import { useEffect, useRef, useState } from "react";
import type { FieldValue, FormValues, GroupFieldSpec, GroupRow } from "../types.js";
import type { LookupResult } from "../api/formApi.js";
import {
  asBoolean,
  blankRow,
  fieldLabel,
  fromRowCell,
  groupErrorKey,
  isLockedRow,
  duplicateNeedsAsking,
  likelyDuplicateRows,
  lockedRowFacts,
  readOnlyShown,
  selfMatchRow,
  selfMatchSeed,
  selfMatchUnseed,
  toRowCellFor,
  validateScalar,
  visibleFields,
} from "../schema/formModel.js";
import { rowSummary, rowTitle } from "../schema/formDesign.js";
import { formatHints } from "../schema/formats.js";
import { rowScope, type PredicateScope } from "../schema/predicates.js";
import { FieldRow, describedByIds } from "./FieldRow.js";
import { ReadOnlyValue } from "./ReadOnlyValue.js";
import { ScalarInput, rowVariantFor } from "./ScalarInput.js";
import "./styles.css";

export interface GroupFieldProps {
  spec: GroupFieldSpec;
  rows: GroupRow[];
  errors: Record<string, string>;
  onRowsChange: (rows: GroupRow[]) => void;
  scope: PredicateScope;
  /** Row indexes a lookup filled in (they carry a "From <source>" chip). */
  prefilledRows?: ReadonlySet<number>;
  prefilledNote?: string;
  /**
   * Phase 541 — the ADDRESS-LOOKUP transport for an address SUBFIELD, handed down unchanged to
   * every row's control. This is the reach a declared `lookups:` entry never had: the existing
   * `fieldAction` / `fieldSlot` slots are per-TOP-LEVEL-field and this component is handed
   * neither, yet a real declaration puts most of its address blocks inside repeating groups.
   *
   * <p>Deliberately passed WHOLE rather than per row: nothing here keys it, because nothing here
   * holds lookup state. Each row's `AddressInput` owns its own busy / candidate / chosen state by
   * React instance identity, so row 2's search cannot fill row 1.</p>
   */
  onAddressLookup?: (postcode: string) => Promise<LookupResult>;
  /**
   * Phase 582 (563 task 5.1) — the form's TOP-LEVEL answers, read ONLY when the group declares
   * `self_match` (its `filler_field` is a top-level field, which this component is otherwise never
   * handed — and, card #3086, its `seed_from` sources are too). Optional: a group without
   * `self_match` never looks at it, so every call site that passes nothing renders exactly as before.
   */
  formValues?: FormValues;
}

/**
 * The `self_match` groups whose tick has already been DELIVERED this form session, keyed by the
 * declaration object — which the page holds for the life of the loaded form, so a step re-entry
 * (a fresh mount of this component) still finds its group here. This is what makes the tick a
 * one-time suggestion: a subject who unticks it, leaves the step and comes back is never
 * re-ticked. The per-mount ran-once ref below guards the same thing within one mount (and React
 * StrictMode's double-invoked effect).
 */
const selfMatchDelivered = new WeakSet<GroupFieldSpec>();

/**
 * The row to self-match on THIS mount, or `null`: the one row naming the filler
 * ({@link selfMatchRow}), provided the tick has not been delivered before and no row already
 * carries the tick (a subject who ticked their own row by hand is never second-guessed).
 */
function pendingSelfMatch(spec: GroupFieldSpec, rows: GroupRow[], formValues: FormValues | undefined): number | null {
  const match = spec.self_match;
  if (!match || selfMatchDelivered.has(spec)) return null;
  if (rows.some((row) => asBoolean(row[match.tick_field] as FieldValue | undefined))) return null;
  return selfMatchRow(spec, rows, formValues ?? {});
}

/**
 * The subfields THIS row shows — the row's own controls, and the list its completeness and its
 * error lookup are both judged against. Per row, never per component: a `row.`-gated subfield
 * is visible in the row whose sibling enabled it and in no other (Phase 515).
 */
function rowSubfields(spec: GroupFieldSpec, row: GroupRow, scope: PredicateScope) {
  return visibleFields(spec.fields, rowScope(scope, row)).filter((field) => (field.type as string) !== "group");
}

/** A row cell as trimmed text (a name cell is a string; anything else reads as ""). */
function asText(cell: GroupRow[string] | undefined): string {
  return typeof cell === "string" ? cell.trim() : "";
}

/** "A", "A and B", "A, B and C" — the other rows' names in a likely-duplicate prompt. */
function listNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

function rowComplete(spec: GroupFieldSpec, row: GroupRow, scope: PredicateScope): boolean {
  for (const sub of rowSubfields(spec, row, scope)) {
    if (validateScalar(sub, fromRowCell(sub.type, row[sub.name]))) return false;
  }
  return true;
}

/**
 * Phase 582 (563 task 4.1) — the words on a collapsed-but-INCOMPLETE row's marker: the label of
 * the FIRST required subfield this row still fails, as `<Label> needed` ("Email needed"). Judged
 * against the same per-row subfield list and the same `validateScalar` as `rowComplete`, so the
 * marker can never disagree with the Done button. A row that fails only on an OPTIONAL subfield
 * (a malformed value, not a gap) names none and falls back to the generic "More details needed".
 * Plain text from the declaration — React escapes it; no HTML is ever injected.
 */
const MORE_DETAILS_NEEDED = "More details needed";
function rowNeededLabel(spec: GroupFieldSpec, row: GroupRow, scope: PredicateScope): string {
  const missing = rowSubfields(spec, row, scope).find(
    (sub) => sub.required === true && validateScalar(sub, fromRowCell(sub.type, row[sub.name])) !== undefined,
  );
  return missing ? `${fieldLabel(missing)} needed` : MORE_DETAILS_NEEDED;
}

/**
 * KYC-design — a REPEATING GROUP as cards: a complete row collapses to its title (from
 * `item_title`) and a one-line summary, with Edit; the row being edited expands with Done and
 * Remove; the add control carries `add_label`. Rows never render as "Parties 1".
 */
export function GroupField({ spec, rows, errors, onRowsChange, scope, prefilledRows, prefilledNote, onAddressLookup, formValues }: GroupFieldProps) {
  const label = fieldLabel(spec);
  const min = Math.max(0, spec.min ?? 0);
  const max = spec.max;
  const canRemove = rows.length > min;
  const canAdd = max === undefined || rows.length < max;
  // The subfield list is per ROW (see `rowSubfields`) — it is computed inside the row loop
  // below, not once here, because two rows of the same group can now show different subfields.
  // OPEN rows are the ones being edited. A row starts open when it is incomplete (a fresh
  // blank row, or a resumed draft with gaps) and collapses only when the client presses
  // Done — never mid-typing. A complete row that arrived complete (a lookup filled it in,
  // or a resumed draft) starts collapsed; Edit reopens it.
  // Rows a lookup filled in start collapsed (the client confirms a card, not a form); every
  // other row — typed, or restored from a draft — starts open so nothing is hidden from the
  // person who wrote it.
  const isPrefilled = (i: number) => prefilledRows?.has(i) === true;
  // Self-match (Phase 582 / 563 5.1) is judged ONCE, against the rows this mount starts with —
  // never re-run as the rows change. The matched row is SEEDED OPEN here, in the pure initialiser,
  // so the subject's own card is expanded on the first paint (and never shows the collapsed
  // "needed" marker) rather than flashing shut until the tick below lands.
  const [selfMatchIndex] = useState<number | null>(() => pendingSelfMatch(spec, rows, formValues));
  const [openRows, setOpenRows] = useState<number[]>(() => {
    const open = rows.map((_, i) => (isPrefilled(i) ? -1 : i)).filter((i) => i >= 0);
    return selfMatchIndex !== null && !open.includes(selfMatchIndex) ? [...open, selfMatchIndex] : open;
  });
  // Keep the open list in step with the rows: a lookup that just wrote rows collapses every
  // one it wrote; rows added outside this component open unless a lookup wrote them; indexes
  // that no longer exist are dropped. One computed list, so the two changes cannot fight.
  const prefilledKey = prefilledRows ? [...prefilledRows].sort((a, b) => a - b).join(",") : "";
  const [seen, setSeen] = useState({ count: rows.length, prefilled: prefilledKey });
  if (rows.length !== seen.count || prefilledKey !== seen.prefilled) {
    let next = openRows.filter((i) => i < rows.length);
    if (prefilledKey !== seen.prefilled) next = next.filter((i) => !isPrefilled(i));
    for (let i = seen.count; i < rows.length; i += 1) if (!isPrefilled(i) && !next.includes(i)) next.push(i);
    setOpenRows(next);
    setSeen({ count: rows.length, prefilled: prefilledKey });
  }

  // The ONE place a subfield answer becomes a row CELL, which is why the subfield's declared
  // TYPE is a parameter: `toRowCellFor` needs it to know that an `address` subfield's cell is
  // the nested object the control handed us, not the empty string a string-only projection
  // made of it (Phase 541).
  function updateRow(index: number, subfield: string, type: string, value: FieldValue) {
    // Card #3090 — TICKING "this is me" by hand claims the row as the filler's own, so its
    // still-blank `seed_from` cells are filled with the filler's answers in the SAME write as the
    // tick — exactly what the recognised row gets on first render (card #3086), for a row the
    // name matcher could not find. Only this row; a filled cell keeps its value.
    // Card #3086 — UNTICKING it hands the row back to someone else, so the cells `seed_from`
    // filled with the filler's own answers (and still holding them) are cleared in the SAME write.
    // Both are judged on the answer the control handed us (`true` / `false`), never on the
    // projected cell, which reads "" for an untick and for an untouched box alike.
    const match = spec.self_match;
    const isTick = !!match && subfield === match.tick_field;
    const seeds = isTick && value === true
      ? selfMatchSeed(spec, rows[index] ?? {}, formValues ?? {})
      : isTick && value === false
        ? selfMatchUnseed(spec, rows[index] ?? {}, formValues ?? {})
        : {};
    writeRow(index, { ...seeds, [subfield]: toRowCellFor(type, value) });
  }
  // ONE row write. Every change to a row goes through here as ONE patch, because `rows` is the
  // render's closed-over list: two writes in one event would each map over the same stale rows
  // and the second would clobber the first.
  function writeRow(index: number, patch: GroupRow) {
    // Typing into a row pins it open: a row shown only because it carried an error must not
    // snap shut the moment the error clears — it closes when the client presses Done.
    setOpenRows((o) => (o.includes(index) ? o : [...o, index]));
    onRowsChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }
  // The tick itself is a write, so it happens after mount, never during render — once, behind a
  // ran-once ref (StrictMode double-invokes effects) and the session-wide delivered set. Empty
  // deps on purpose: this is the initial-load suggestion, not a rule kept true as rows change.
  // Card #3086 — the `seed_from` cells ride the SAME patch as the tick (see `writeRow`), so they
  // too land once, on this one row, and never again on a re-render or a step re-entry. (A row
  // the subject ticks BY HAND is seeded by `updateRow` instead — card #3090.)
  const selfMatchRan = useRef(false);
  useEffect(() => {
    if (selfMatchRan.current) return;
    selfMatchRan.current = true;
    if (selfMatchIndex === null || !spec.self_match || selfMatchDelivered.has(spec)) return;
    selfMatchDelivered.add(spec);
    const seeds = selfMatchSeed(spec, rows[selfMatchIndex] ?? {}, formValues ?? {});
    writeRow(selfMatchIndex, { ...seeds, [spec.self_match.tick_field]: toRowCellFor("boolean", true) });
  }, []);

  function addRow() {
    onRowsChange([...rows, blankRow(spec)]);
    setOpenRows((o) => [...o, rows.length]);
    setSeen((s) => ({ ...s, count: rows.length + 1 }));
  }
  function removeRow(index: number) {
    onRowsChange(rows.filter((_, i) => i !== index));
    setOpenRows((o) => o.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i)));
    setSeen((s) => ({ ...s, count: rows.length - 1 }));
  }
  function closeRow(index: number) { setOpenRows((o) => o.filter((i) => i !== index)); }

  // Card #2506 (ARD-DSL-084) — rows that look like another row's person. Judged on every render
  // from the rows as they are, so removing one of a pair clears the other's prompt at once.
  const duplicates = likelyDuplicateRows(spec, rows, scope);
  function duplicatePrompt(index: number, title: string) {
    const others = duplicates.get(index);
    const decl = spec.likely_duplicates;
    if (!others || !decl) return null;
    const names = others.map((o) => asText(rows[o]![decl.name_field]) || rowTitle(spec, rows[o]!, o, label));
    const id = `${spec.name}-duplicate-${index}`;
    // Review note 2 — names that still differ beyond the first forename (other middle names, or
    // a generation on one side only) may be two people: ask, never assert, and say to keep both.
    const mine = asText(rows[index]![decl.name_field]);
    const ask = others.some((o) => duplicateNeedsAsking(mine, asText(rows[o]![decl.name_field])));
    return (
      <div className="group-row-duplicate" role="note" id={id} data-testid={id}>
        <p className="group-row-duplicate-title">
          {ask ? `Is this the same person as ${listNames(names)}?` : `This looks like the same person as ${listNames(names)}.`}
        </p>
        <p className="group-row-duplicate-note">{decl.note}</p>
        {ask ? <p className="group-row-duplicate-note">{names.length > 1 ? "If not, keep them all." : "If not, keep both."}</p> : null}
        <button
          type="button"
          className="group-link group-link-remove"
          disabled={!canRemove}
          onClick={() => removeRow(index)}
          aria-label={ask ? `Remove ${title} if it is the same person as ${listNames(names)}` : `Remove ${title}, the same person as ${listNames(names)}`}
        >
          Remove this one
        </button>
      </div>
    );
  }
  function openRow(index: number) { setOpenRows((o) => (o.includes(index) ? o : [...o, index])); }

  return (
    <FieldRow
      name={spec.name}
      label={label}
      required={spec.required ?? min > 0}
      error={errors[spec.name]}
      help={spec.help}
      helpLink={spec.help_link}
      variant="group"
    >
      <div className="group-rows">
        {rows.map((row, index) => {
          // Walk 3 (ARD-DSL-070) — a LOCKED row is a read-only card of facts: no inputs, no Edit,
          // no Remove, never the "needed" marker. It is complete by definition.
          if (isLockedRow(spec, row, scope)) {
            const locked = spec.locked_rows!;
            const title = rowTitle(spec, row, index, label);
            const facts = lockedRowFacts(spec, row);
            return (
              <div className="group-row group-row-locked" key={`${spec.name}-${index}`} data-testid={`${spec.name}-locked-${index}`}>
                <div className="group-row-head">
                  <span className="group-row-mark group-row-mark-locked" aria-hidden="true" />
                  <div className="group-row-titles">
                    <span className="chip group-row-eyebrow">{locked.eyebrow}</span>
                    <p className="group-row-title">{title}</p>
                  </div>
                </div>
                {facts.length > 0 && (
                  <dl className="group-row-facts">
                    {facts.map((fact) => (
                      <div className="group-row-fact" key={fact.name}>
                        <dt>{fact.label}</dt>
                        <dd>{fact.display}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                {locked.note && <p className="group-row-note">{locked.note}</p>}
              </div>
            );
          }
          const subfields = rowSubfields(spec, row, scope);
          const complete = rowComplete(spec, row, scope);
          const hasErrors = subfields.some((sub) => errors[groupErrorKey(spec.name, index, sub.name)]);
          const open = openRows.includes(index) || hasErrors;
          const title = rowTitle(spec, row, index, label);
          const summary = rowSummary(spec, row);
          const chip = prefilledRows?.has(index) && prefilledNote ? <span className="chip">{prefilledNote}</span> : null;
          if (!open) {
            // A collapsed row that is still INCOMPLETE (a lookup filled it in without, say, an
            // email) must not wear the done tick: it shows a "<Label> needed" chip instead. The
            // open/collapsed decision above is untouched — only the collapsed row's marker differs,
            // and a complete collapsed row renders exactly as it always has.
            const needed = complete ? null : rowNeededLabel(spec, row, scope);
            return (
              <div className="group-row" key={`${spec.name}-${index}`}>
                <div className="group-row-head">
                  <span className={needed === null ? "group-row-mark group-row-mark-done" : "group-row-mark group-row-mark-needed"} aria-hidden="true" />
                  <div className="group-row-titles">
                    <p className="group-row-title">{title}</p>
                    {summary && <p className="group-row-summary">{summary}</p>}
                    {needed !== null && <span className="group-row-needed" title={MORE_DETAILS_NEEDED}>{needed}</span>}
                  </div>
                  <div className="group-row-actions">
                    {chip}
                    <button type="button" className="group-link" onClick={() => openRow(index)} aria-label={`Edit ${title}`}>Edit</button>
                  </div>
                </div>
                {duplicatePrompt(index, title)}
              </div>
            );
          }
          return (
            <div className="group-row group-row-editing" key={`${spec.name}-${index}`}>
              <div className="group-row-head">
                <span className="group-row-mark" aria-hidden="true" />
                <div className="group-row-titles"><p className="group-row-title">{title}</p></div>
                <div className="group-row-actions">
                  {chip}
                  <button
                    type="button"
                    className="group-link group-link-remove"
                    disabled={!canRemove}
                    onClick={() => removeRow(index)}
                    aria-label={`Remove ${title}`}
                  >
                    Remove
                  </button>
                </div>
              </div>
              {duplicatePrompt(index, title)}
              {subfields.map((sub) => {
                const rowId = `${spec.name}__${index}__${sub.name}`;
                // Phase 572 wave 6 task 6.2 — a read_only SUBFIELD takes the same route as a
                // top-level one (FormRenderer): the ONE ReadOnlyValue, or nothing at all when this
                // row's cell holds no value. The row's chip stays in the row head.
                if (sub.read_only === true) {
                  const shown = readOnlyShown(sub, row);
                  if (shown === null) return null;
                  return (
                    <FieldRow key={sub.name} name={rowId} label={fieldLabel(sub)} required={true} help={sub.help} helpLink={sub.help_link} variant="field">
                      <ReadOnlyValue id={rowId} {...shown} />
                    </FieldRow>
                  );
                }
                const error = errors[groupErrorKey(spec.name, index, sub.name)];
                // Same read-back rule as a top-level row: a display preset describes whatever
                // the cell currently holds (FormRenderer.tsx, formats.ts formatHints).
                const hints = formatHints(sub.format, row[sub.name]);
                return (
                  <FieldRow
                    key={sub.name}
                    name={rowId}
                    label={fieldLabel(sub)}
                    required={sub.required ?? false}
                    error={error}
                    help={sub.help}
                    helpLink={sub.help_link}
                    hints={hints}
                    variant={rowVariantFor(sub.type)}
                  >
                    <ScalarInput
                      spec={sub}
                      id={rowId}
                      value={fromRowCell(sub.type, row[sub.name])}
                      invalid={error !== undefined}
                      describedBy={describedByIds(rowId, { help: sub.help, error, hints })}
                      onChange={(value) => updateRow(index, sub.name, sub.type, value)}
                      onAddressLookup={onAddressLookup}
                    />
                  </FieldRow>
                );
              })}
              {complete && (
                <button type="button" className="group-done" onClick={() => closeRow(index)}>Done</button>
              )}
            </div>
          );
        })}
      </div>
      <button type="button" className="group-add" disabled={!canAdd} onClick={addRow}>
        {spec.add_label ?? `Add ${label}`}
      </button>
    </FieldRow>
  );
}
